/**
 * Canvas drawing for take-off measurement markup (export snippets).
 * Mirrors MeasurementLayer geometry in pixel space on a rendered PDF page canvas.
 */

import type { PdfMeasurement, PdfTakeoffLink } from '../types';
import type { NormBBox, NormPoint } from './takeoffMarkupCanvasTypes';

export type { NormBBox, NormPoint } from './takeoffMarkupCanvasTypes';

const STROKE_LINKED = '#22c55e';
const PIN_FILL = '#f97316';
const PIN_STROKE = '#ffffff';
/** Match MeasurementLayer take-off pill sizing (see TAKEOFF_LABEL_* constants). */
const TAKEOFF_LABEL_FONT_PX = 11;
const TAKEOFF_DIM_FONT_PX = 10;
const TAKEOFF_LABEL_PAD_X_PX = 8;
const TAKEOFF_LABEL_PAD_Y_PX = 4;
const STROKE_WIDTH_PX = 2;
const ENDPOINT_DOT_PX = 3.5;

function exportRenderScale(canvasWidth: number, cropBBox: NormBBox): number {
  // Typical letter page ≈ 612pt; render scale is roughly canvasWidth / 612.
  const pageRenderScale = canvasWidth / 612;
  // Cropped snippets zoom in — shrink labels so they match on-screen take-off proportions.
  const cropFraction = Math.min(1, Math.max(0.12, (cropBBox.maxX - cropBBox.minX) / 100));
  return Math.max(1, pageRenderScale * cropFraction * 0.92);
}

function normToCanvas(p: NormPoint, width: number, height: number): { x: number; y: number } {
  return { x: (p.x / 100) * width, y: (p.y / 100) * height };
}

function canvasToNorm(x: number, y: number, width: number, height: number): NormPoint {
  return { x: (x / width) * 100, y: (y / height) * 100 };
}

function pointsToPath(
  ctx: CanvasRenderingContext2D,
  points: NormPoint[],
  width: number,
  height: number,
  close = false,
): void {
  if (points.length < 2) return;
  const first = normToCanvas(points[0], width, height);
  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < points.length; i++) {
    const p = normToCanvas(points[i], width, height);
    ctx.lineTo(p.x, p.y);
  }
  if (close) ctx.closePath();
}

export function computeNormBBox(points: NormPoint[], paddingRatio = 0.1): NormBBox | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const span = Math.max(maxX - minX, maxY - minY, 8);
  const pad = span * paddingRatio;
  return {
    minX: Math.max(0, minX - pad),
    minY: Math.max(0, minY - pad),
    maxX: Math.min(100, maxX + pad),
    maxY: Math.min(100, maxY + pad),
  };
}

function mergeBboxes(a: NormBBox, b: NormBBox): NormBBox {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

function labelPlacement(m: Pick<PdfMeasurement, 'kind' | 'points'>): NormPoint {
  if (m.kind === 'linear' && m.points.length >= 2) {
    const [a, b] = m.points;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - 2.5 };
  }
  if (m.kind === 'polyline' && m.points.length >= 2) {
    const mid = m.points[Math.floor(m.points.length / 2)];
    return { x: mid.x, y: mid.y - 2.5 };
  }
  if (m.kind === 'angle' && m.points.length >= 3) {
    return { x: m.points[1].x, y: m.points[1].y - 3 };
  }
  let cx = 0;
  let cy = 0;
  for (const p of m.points) {
    cx += p.x;
    cy += p.y;
  }
  return { x: cx / m.points.length, y: cy / m.points.length };
}

/** Estimate label footprint in norm space so crops include pills. */
function estimateLabelBBox(
  placement: NormPoint,
  title: string,
  sub?: string,
  labelPadNorm = 4,
): NormBBox {
  const titleLen = Math.max(title.length, sub?.length ?? 0);
  const widthNorm = Math.min(18, Math.max(8, titleLen * 0.62));
  const heightNorm = sub ? 5.5 : 4;
  return {
    minX: Math.max(0, placement.x - widthNorm / 2),
    maxX: Math.min(100, placement.x + widthNorm / 2),
    minY: Math.max(0, placement.y - heightNorm - labelPadNorm),
    maxY: Math.min(100, placement.y + 2),
  };
}

function drawLabelPill(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  norm: NormPoint,
  title: string,
  sub: string | undefined,
  renderScale: number,
): NormBBox {
  const fontSize = Math.max(8, Math.round(TAKEOFF_LABEL_FONT_PX * renderScale));
  const subFontSize = Math.max(7, Math.round(TAKEOFF_DIM_FONT_PX * renderScale));
  const padX = TAKEOFF_LABEL_PAD_X_PX * renderScale;
  const padY = TAKEOFF_LABEL_PAD_Y_PX * renderScale;
  const lineGap = 2 * renderScale;
  const { x, y } = normToCanvas(norm, width, height);

  ctx.font = `600 ${fontSize}px Helvetica, Arial, sans-serif`;
  const titleW = ctx.measureText(title).width;
  let subW = 0;
  if (sub) {
    ctx.font = `${subFontSize}px Helvetica, Arial, sans-serif`;
    subW = ctx.measureText(sub).width;
  }
  const boxW = Math.max(titleW, subW) + padX * 2;
  const boxH = sub
    ? fontSize + lineGap + subFontSize + padY * 2
    : fontSize + padY * 2;
  const left = x - boxW / 2;
  const top = y - boxH - fontSize * 0.35;
  const radius = Math.min(boxH / 2, 10 * renderScale);

  ctx.fillStyle = 'rgba(34, 197, 94, 0.92)';
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(left, top, boxW, boxH, radius);
  } else {
    ctx.rect(left, top, boxW, boxH);
  }
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.font = `600 ${fontSize}px Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(title, x, sub ? top + padY + fontSize / 2 : top + padY + fontSize / 2);
  if (sub) {
    ctx.font = `${subFontSize}px Helvetica, Arial, sans-serif`;
    ctx.fillText(sub, x, top + padY + fontSize + lineGap + subFontSize / 2);
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  return {
    minX: canvasToNorm(left, top, width, height).x,
    minY: canvasToNorm(left, top, width, height).y,
    maxX: canvasToNorm(left + boxW, top + boxH, width, height).x,
    maxY: canvasToNorm(left + boxW, top + boxH, width, height).y,
  };
}

function drawPin(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  pin: NormPoint,
  label: string | undefined,
  renderScale: number,
): NormBBox {
  const { x, y } = normToCanvas(pin, width, height);
  const r = Math.max(3, 5 * renderScale * 0.45);
  ctx.fillStyle = PIN_FILL;
  ctx.strokeStyle = PIN_STROKE;
  ctx.lineWidth = Math.max(1, 1.5 * renderScale * 0.45);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  if (label) {
    const pinFont = Math.max(7, Math.round(TAKEOFF_LABEL_FONT_PX * renderScale * 0.9));
    ctx.font = `600 ${pinFont}px Helvetica, Arial, sans-serif`;
    ctx.fillStyle = PIN_FILL;
    ctx.textAlign = 'center';
    ctx.fillText(label, x, y - r * 1.6);
    ctx.textAlign = 'left';
  }
  const normR = (r / width) * 100;
  return {
    minX: pin.x - normR * 2,
    minY: pin.y - normR * 3,
    maxX: pin.x + normR * 2,
    maxY: pin.y + normR,
  };
}

function formatPieceLabel(link: PdfTakeoffLink): string {
  const name = link.name?.trim() || 'Piece';
  return link.quantity > 1 ? `${name} × ${link.quantity}` : name;
}

export interface DrawTakeoffMarkupOptions {
  ctx: CanvasRenderingContext2D;
  canvasWidth: number;
  canvasHeight: number;
  measurements: PdfMeasurement[];
  linksByMeasurementId: Map<string, PdfTakeoffLink>;
  structurePin?: NormPoint | null;
  structureName?: string;
  /** Bbox used to scale labels for cropped export snippets (typically pre-render estimate). */
  labelScaleBBox?: NormBBox;
}

/** Draw linked take-off measurements and labels onto a rendered PDF page canvas. */
export function drawTakeoffMarkupOnCanvas(options: DrawTakeoffMarkupOptions): NormBBox | null {
  const {
    ctx,
    canvasWidth: w,
    canvasHeight: h,
    measurements,
    linksByMeasurementId,
    structurePin,
    structureName,
    labelScaleBBox,
  } = options;

  const scaleBBox = labelScaleBBox ?? { minX: 0, minY: 0, maxX: 100, maxY: 100 };
  const renderScale = exportRenderScale(w, scaleBBox);
  const strokeWidth = Math.max(1, STROKE_WIDTH_PX * renderScale * 0.5);
  const endpointDot = Math.max(2, ENDPOINT_DOT_PX * renderScale * 0.5);
  let combinedBbox: NormBBox | null = null;

  const addPoints = (points: NormPoint[]) => {
    const box = computeNormBBox(points, 0.04);
    if (!box) return;
    combinedBbox = combinedBbox ? mergeBboxes(combinedBbox, box) : box;
  };

  const addBox = (box: NormBBox) => {
    combinedBbox = combinedBbox ? mergeBboxes(combinedBbox, box) : box;
  };

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const m of measurements) {
    if (!m.id || !linksByMeasurementId.has(m.id)) continue;
    const link = linksByMeasurementId.get(m.id)!;
    addPoints(m.points);

    ctx.strokeStyle = STROKE_LINKED;
    ctx.lineWidth = strokeWidth;

    if (m.kind === 'linear' && m.points.length >= 2) {
      pointsToPath(ctx, m.points, w, h);
      ctx.stroke();
      for (const p of m.points) {
        const c = normToCanvas(p, w, h);
        ctx.fillStyle = STROKE_LINKED;
        ctx.beginPath();
        ctx.arc(c.x, c.y, endpointDot, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (m.kind === 'polyline') {
      pointsToPath(ctx, m.points, w, h);
      ctx.stroke();
    } else if (m.kind === 'area' && m.points.length >= 3) {
      pointsToPath(ctx, m.points, w, h, true);
      ctx.stroke();
    } else if (m.kind === 'angle' && m.points.length >= 3) {
      pointsToPath(ctx, m.points, w, h);
      ctx.stroke();
    }

    const placement = labelPlacement(m);
    const title = formatPieceLabel(link);
    addBox(estimateLabelBBox(placement, title));
    addBox(drawLabelPill(ctx, w, h, placement, title, undefined, renderScale));
  }

  if (structurePin) {
    addBox(drawPin(ctx, w, h, structurePin, structureName, renderScale));
  }

  return combinedBbox;
}

/** Crop a region from a canvas using normalized 0–100 coordinates. */
export function cropCanvasToNormBBox(source: HTMLCanvasElement, bbox: NormBBox): HTMLCanvasElement {
  const sx = (bbox.minX / 100) * source.width;
  const sy = (bbox.minY / 100) * source.height;
  const sw = ((bbox.maxX - bbox.minX) / 100) * source.width;
  const sh = ((bbox.maxY - bbox.minY) / 100) * source.height;
  const dest = document.createElement('canvas');
  dest.width = Math.max(1, Math.round(sw));
  dest.height = Math.max(1, Math.round(sh));
  const ctx = dest.getContext('2d');
  if (!ctx) return dest;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, dest.width, dest.height);
  return dest;
}

/** Max pixel width for embedded snippet images — enough for letter-size print. */
export const SNIPPET_EXPORT_MAX_WIDTH_PX = 2200;
export const SNIPPET_JPEG_QUALITY = 0.84;

function downscaleCanvasForExport(
  canvas: HTMLCanvasElement,
  maxWidth: number,
): HTMLCanvasElement {
  if (canvas.width <= maxWidth) return canvas;
  const scale = maxWidth / canvas.width;
  const dest = document.createElement('canvas');
  dest.width = Math.max(1, Math.round(canvas.width * scale));
  dest.height = Math.max(1, Math.round(canvas.height * scale));
  const ctx = dest.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, dest.width, dest.height);
  return dest;
}

/** Downscale + JPEG encode for smaller, faster PDF exports. */
export async function canvasToJpegBytes(
  canvas: HTMLCanvasElement,
  maxWidth = SNIPPET_EXPORT_MAX_WIDTH_PX,
  quality = SNIPPET_JPEG_QUALITY,
): Promise<{ bytes: Uint8Array; widthPx: number; heightPx: number }> {
  const scaled = downscaleCanvasForExport(canvas, maxWidth);
  const blob = await new Promise<Blob>((resolve, reject) => {
    scaled.toBlob(
      result => (result ? resolve(result) : reject(new Error('Failed to encode JPEG'))),
      'image/jpeg',
      quality,
    );
  });
  const buffer = await blob.arrayBuffer();
  return { bytes: new Uint8Array(buffer), widthPx: scaled.width, heightPx: scaled.height };
}

/** Pre-compute crop bbox from measurement geometry + estimated labels (before render). */
export function estimateMarkupNormBBox(
  measurements: PdfMeasurement[],
  linksByMeasurementId: Map<string, PdfTakeoffLink>,
  structurePin?: NormPoint | null,
): NormBBox {
  let combined: NormBBox | null = null;
  const merge = (box: NormBBox) => {
    combined = combined ? mergeBboxes(combined, box) : box;
  };

  for (const m of measurements) {
    if (!m.id || !linksByMeasurementId.has(m.id)) continue;
    const link = linksByMeasurementId.get(m.id)!;
    const ptsBox = computeNormBBox(m.points, 0.06);
    if (ptsBox) merge(ptsBox);
    const placement = labelPlacement(m);
    const title = formatPieceLabel(link);
    merge(estimateLabelBBox(placement, title));
  }

  if (structurePin) {
    merge({
      minX: Math.max(0, structurePin.x - 4),
      maxX: Math.min(100, structurePin.x + 4),
      minY: Math.max(0, structurePin.y - 6),
      maxY: Math.min(100, structurePin.y + 2),
    });
  }

  return combined ?? { minX: 0, minY: 0, maxX: 100, maxY: 100 };
}
